import {
    HttpClient,
    HttpErrorResponse,
    HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastService } from '@blocks/large-toast/toast.service';
import { SpinnerService } from '@blocks/spinner/spinner.service';
import { environment } from '@environments/environment';
import { AppResponse } from '@models/api';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/internal/operators/catchError';
import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class ApiBaseService {
    private defaultVersion: string = environment.defaultApiVersion;

    constructor(
        private httpClient: HttpClient,
        private spinnerService: SpinnerService,
        private toasterService: ToastService,
    ) {}

    public get<T>(
        path: string[],
        options?: {
            templateValues?: object;
            version?: string;
            params?: HttpParams;
            isLoading?: boolean;
        },
    ): Observable<T> {
        const version = options?.version ?? this.defaultVersion;
        const apiPath = ApiBaseService.getApiUrl(
            environment.baseUrl,
            path.join('/'),
            version,
            options?.templateValues,
        );

        const params = options?.params;

        if (options?.isLoading) this.spinnerService.show();

        return this.httpClient.get(apiPath, { params }).pipe(
            map((data) => {
                const apiData = data as AppResponse<T>;
                if (options?.isLoading) this.spinnerService.hide();
                return apiData.data as T;
            }),
            catchError((err) => {
                this.showErrors(err.error);
                if (options?.isLoading) this.spinnerService.hide();
                return throwError(err);
            }),
        );
    }

    public post<T>(
        path: string[],
        body: any,
        options?: {
            templateValues?: object;
            version?: string;
            params?: HttpParams;
            isLoading?: boolean;
            isShowToast?: boolean;
            toastMessage?: string;
            withMeta?: boolean;
        },
    ): Observable<T> {
        const version = options?.version ?? this.defaultVersion;

        const apiPath = ApiBaseService.getApiUrl(
            environment.baseUrl,
            path.join('/'),
            version,
            options?.templateValues,
        );
        const params = options?.params;

        if (options?.isLoading) this.spinnerService.show();

        return this.httpClient.post(apiPath, body, { params }).pipe(
            map((data) => {
                const apiData = data as any;
                if (apiData?.data == null) {
                    throw new HttpErrorResponse({
                        error: 'Data not found',
                        status: 404,
                    });
                }
                if (options?.isLoading) this.spinnerService.hide();
                if (options?.isShowToast)
                    this.toasterService.success(
                        options?.toastMessage || 'successfully created',
                    );

                if (options?.withMeta) return apiData as T;

                return apiData?.data as T;
            }),
            catchError((err) => {
                this.showErrors(err.error);
                if (options?.isLoading) this.spinnerService.hide();
                return throwError(err);
            }),
        );
    }

    public put<T>(
        path: string[],
        body: any,
        options?: {
            templateValues?: object;
            version?: string;
            params?: HttpParams;
            isLoading?: boolean;
            isShowToast?: boolean;
            toastMessage?: string;
            withMeta?: boolean;
        },
    ): Observable<T> {
        const version = options?.version ?? this.defaultVersion;

        const apiPath = ApiBaseService.getApiUrl(
            environment.baseUrl,
            path.join('/'),
            version,
            options?.templateValues,
        );

        const params = options?.params;

        if (options?.isLoading) this.spinnerService.show();

        return this.httpClient.put(apiPath, body, { params }).pipe(
            map((data) => {
                const apiData = data as any;
                if (apiData?.data == null) {
                    throw new HttpErrorResponse({
                        error: 'Data not found',
                        status: 404,
                    });
                }
                if (options?.isLoading) this.spinnerService.hide();

                if (options?.isShowToast)
                    this.toasterService.success(
                        options?.toastMessage || 'successfully updated.',
                    );

                if (options?.withMeta) return apiData as T;

                return apiData?.data as T;
            }),
            catchError((err) => {
                this.showErrors(err.error);
                if (options?.isLoading) this.spinnerService.hide();
                return throwError(err);
            }),
        );
    }

    public delete<T>(
        path: string[],
        options?: {
            templateValues?: object;
            version?: string;
            params?: HttpParams;
            isLoading?: boolean;
            isShowToast?: boolean;
            toastMessage?: string;
        },
    ): Observable<T | undefined> {
        const version = options?.version ?? this.defaultVersion;
        const apiPath = ApiBaseService.getApiUrl(
            environment.baseUrl,
            path.join('/'),
            version,
            options?.templateValues,
        );

        const params = options?.params;
        if (options?.isLoading) this.spinnerService.show();

        return this.httpClient.delete(apiPath, { params }).pipe(
            map((data) => {
                const apiData = data as AppResponse<T>;
                if (options?.isLoading) this.spinnerService.hide();

                if (options?.isShowToast)
                    this.toasterService.success(
                        options?.toastMessage || 'successfully deleted.',
                    );

                return apiData.data;
            }),
            catchError((err) => {
                this.showErrors(err.error);
                if (options?.isLoading) this.spinnerService.hide();
                return throwError(err);
            }),
        );
    }

    private static getApiUrl(
        baseUrl: string,
        path: string,
        version: string,
        templateValues?: object,
    ): string {
        let url = '';
        if (baseUrl.includes('api/v')) {
            url = baseUrl + `${version}` + path;
        } else {
            url = `${baseUrl}/api/${version}/${path}`;
        }

        if (templateValues) {
            for (const key of Object.keys(templateValues)) {
                url = url.replace('{' + key + '}', templateValues[key]);
            }
        }

        return url;
    }

    public getDefaultUrl(path: string): string {
        return ApiBaseService.getApiUrl(
            environment.baseUrl,
            path,
            this.defaultVersion,
            {},
        );
    }

    private showErrors(error: AppResponse<any>): void {
        if (error != null && !error.meta?.isInvalidToken) {
            this.toasterService.danger(
                error.meta?.message ??
                    'Error occurred while processing request.',
            );
        }
    }
}
